import { type NavItem, goItem, groupForSection, navigation } from "@/fixtures/navigation";

/**
 * Wat elk nog niet gebouwd scherm nodig heeft, kort (ontwerp 28-10-2026,
 * scherm 36 "Nog te bouwen"). De lange uitleg staat bij de placeholder in
 * routes.tsx; dit is de samenvatting in één regel voor het overzicht. Een
 * scherm dat hier ontbreekt, krijgt in het overzicht "zie de placeholder".
 *
 * Welke schermen nog te bouwen zijn, bepaalt het menu (`built: false` in
 * fixtures/navigation.ts), niet deze lijst.
 */
export const TODO_NEEDS: Record<string, string> = {
  "/koers": "Doelen per thema als keuze van de corporatie in Parameters & rekenregels",
  "/scenarios": "Scenarioscherm op de programma's P1 tot en met P22",
  "/planning": "Scenarioscherm en v70-kasstroomruns per scenario",
  "/wens-game": "Doelfunctie met gewichten per deelnemer",
  "/ontwerp-game": "Keuze voor de 3D-ontwerpomgeving (IFC)",
  "/portefeuille-game": "Scenarioscherm en doorrekening naar portefeuille",
  "/conditie": "Conditiemetingen per element",
  "/desinvestering": "Scherm op P14 tot en met P17",
  "/meerjarenbegroting": "Drie stromen in één leesmodel",
  "/begroting-vergelijken": "Actuals uit het ERP",
  "/huurpotentie": "Eigen scherm en een verse huurexport",
  "/mutaties": "Verhuurgegevens per VHE",
  "/leegstand": "Leegstand per VHE",
  "/derving": "Derving per VHE",
  "/woningmarkt": "Bronkeuze CBS of ABF",
  "/beleid": "Koppeling met het Omgevingsloket",
  "/rapportages": "Module 14, inclusief dPi/dVi-export",
  "/data-ai": "Besluit over de weging van de score",
  "/atlas-ai": "AI-platform en de tien vragen",
  "/audittrail": "Alleen het scherm en een leesroute",
  "/go": "Eigen fase: app of PWA, offline",
};

export type TodoGroup = {
  /** Het onderdeel uit het menu, of "Altijd onderin" voor Atlas-GO. */
  title: string;
  /** De groep in de zijbalk (Ons bezit, Sturen en plannen, Verantwoorden). */
  group: string;
  items: Array<NavItem & { needs: string }>;
};

/** Volgorde in het overzicht: eerst sturen en plannen, dan bezit, dan verantwoorden (de mockup). */
const GROEPVOLGORDE = ["Sturen en plannen", "Ons bezit", "Verantwoorden", "Overzicht"];

/** Onder Verantwoorden zijn de onderdelen klein; die staan samen in één blok, zoals in de mockup. */
const SAMEN = "Verantwoorden";

/**
 * De nog te bouwen schermen per onderdeel, uit het menu. Adminschermen tellen
 * mee als ze nog te bouwen zijn; een beheerder ziet dit overzicht toch.
 */
export function todoGroups(): TodoGroup[] {
  const metBehoefte = (item: NavItem) => ({ ...item, needs: TODO_NEEDS[item.to] ?? "Zie de placeholder" });
  const groepen: TodoGroup[] = [];
  for (const section of navigation) {
    const items = section.items.filter((item) => item.built === false).map(metBehoefte);
    if (items.length === 0) continue;
    const group = groupForSection(section)?.title ?? "";
    const samen = group === SAMEN ? groepen.find((groep) => groep.title === SAMEN) : undefined;
    if (samen) samen.items.push(...items);
    else groepen.push({ title: group === SAMEN ? SAMEN : section.title, group, items });
  }
  // Stabiel sorteren: binnen een groep blijft de menuvolgorde staan.
  groepen.sort((a, b) => GROEPVOLGORDE.indexOf(a.group) - GROEPVOLGORDE.indexOf(b.group));
  if (goItem.built === false) groepen.push({ title: "Altijd onderin", group: "", items: [metBehoefte(goItem)] });
  return groepen;
}

/** Het aantal nog te bouwen schermen in het menu. */
export function todoCount(groepen: TodoGroup[] = todoGroups()): number {
  return groepen.reduce((som, groep) => som + groep.items.length, 0);
}
